import { useEventPickerDialog } from '@apps/registry/common/components/EventPicker';
import type { CatalogStorefront, EventUserStatus } from '@graphql/generated-base';
import { CustomItemDataType } from '@graphql/generated-base';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import { withWindow } from '@shared/utils/withWindow';
import { useCallback, useEffect, useState } from 'react';
import type { EventMembership } from '@shared/components/AuthProvider';
import type { CashOrCharityAddAttempt, DeliverableAddAttempt, TrackAttemptPayload } from '../useAddItemToRegistryAttempt';
import { isDeliverableAddAttempt } from '../useAddItemToRegistryAttempt/isDeliverableAddAttempt';
import qs from 'query-string';
import { usePublicShopTelemetry } from '../Shop.telemetry';
import type { UseEventPickerDialogArgs } from '@apps/registry/common/components/EventPicker/EventPickerDialog';
import type { MinimalProductWithDetails, UseCreateAddRegistryItemClickPayloadReturn } from '@apps/registry/common/components/Catalog/utils/createTelemetryPayload';
import { useCreateAddRegistryItemClickPayload } from '@apps/registry/common/components/Catalog/utils/createTelemetryPayload';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AddRegistryItemClickedProps } from '@apps/registry/common/components/Catalog';
import { useQueryParamHelper } from '@shared/core/queryString';
import { getInstanceId } from '@shared/core/analytics/segment/getTrackerIds';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import { useSegmentGroupCallForEvent } from '@shared/core';
import { useSetEventHandle } from '@shared/utils/eventInfo';
import { useRedirectToFriendsRegistry } from '@apps/registry/common/components/FriendsRegistry/hooks/useRedirectToFriendsRegistry';
import { isDeliverableItemType, isCashOrCharityItemType } from '@apps/registry/common/components/Catalog/utils/interpretItemType';
import { universalItemSourceToRegistryItemSource } from '@apps/registry/common/components/Catalog/utils/registryItemMetadata';

const { isDevelopment } = getNodeEnv();

const BLISS_APP_ORIGIN = withWindow(global => {
  return isDevelopment ? 'http://localhost:3000' : global.location.origin;
}, '');
const PUBLIC_SHOP_ORIGIN = withWindow(global => {
  return isDevelopment ? 'http://localhost:3000' : global.location.origin;
}, '');

type UseEventAssociatorArgs = {
  membershipStatus?: EventUserStatus | 'adminOrOwner' | 'all';
  addToRegistryAttemptInfo: TrackAttemptPayload<MinimalProductWithDetails> | null;
};

export type EventAssociatorState = {
  eventId?: string;
  eventHandle?: string;
};

type PrepareAddAttemptInfoArgs = {
  eventId: string | undefined;
  addToRegistryAttemptInfo: UseEventAssociatorArgs['addToRegistryAttemptInfo'];
  deriveStandardProductPayload: UseCreateAddRegistryItemClickPayloadReturn['deriveStandardProductPayload'];
  searchQuery: string | undefined;
  refQueryParam: string | undefined;
  rootShopPathName: string;
  currentStorefront: CatalogStorefront;
};

const prepareAddAttemptInfo = ({
  eventId,
  addToRegistryAttemptInfo,
  deriveStandardProductPayload,
  searchQuery,
  refQueryParam,
  rootShopPathName,
  currentStorefront,
}: PrepareAddAttemptInfoArgs) => {
  let shopPathname = `${PUBLIC_SHOP_ORIGIN}${rootShopPathName}`;
  let shopAuthenticatePayload: Maybe<AddRegistryItemClickedProps>;

  if (addToRegistryAttemptInfo) {
    let shouldSkip = false;
    let additionalQueryParamPayload: Record<string, Maybe<string | number | boolean>> = {};

    //=========================================================
    // Add source: from catalog (quick add, pdp modal)
    //=========================================================
    if (addToRegistryAttemptInfo.type === 'catalog') {
      shopPathname = addToRegistryAttemptInfo.catalogLocator ? `${PUBLIC_SHOP_ORIGIN}${addToRegistryAttemptInfo.catalogLocator}` : shopPathname;

      //==========================================
      // Catalog Item Type: Something We Deliver (product, gift_card, etc.)
      //==========================================
      if (isDeliverableItemType(addToRegistryAttemptInfo.itemType)) {
        // Adding from Product Grid view when there is no quick add option (EG. custom Cash fund)
        const { product, category, curation, qty, itemPosition, telemetryArgs } = addToRegistryAttemptInfo as DeliverableAddAttempt;
        shopAuthenticatePayload = deriveStandardProductPayload({
          ...telemetryArgs,
          eventId,
          product,
          itemType: product.itemType as CustomItemDataType,
          itemPosition,
          selectedCategory: category,
          storedCuration: curation,
          qty,
        });

        additionalQueryParamPayload = {
          productId: product.id,
          qty,
        };
      }

      //==========================================
      // Catalog Item Type: Cash Fund / Charity items
      //==========================================
      else if (isCashOrCharityItemType(addToRegistryAttemptInfo.itemType)) {
        // Adding from Product Grid view when there is no quick add option (EG. custom Cash fund)
        const { product, category, curation, isCreateFromCustom, itemPosition, telemetryArgs } = addToRegistryAttemptInfo as CashOrCharityAddAttempt;
        shopAuthenticatePayload = deriveStandardProductPayload({
          ...telemetryArgs,
          eventId,
          product,
          itemType: product?.itemType as CustomItemDataType,
          itemPosition,
          selectedCategory: category,
          storedCuration: curation,
          qty: 1,
        });

        additionalQueryParamPayload = {
          productId: product?.id,
          custom: isCreateFromCustom ? 'true' : null,
          fundType: addToRegistryAttemptInfo.itemType,
        };
      }
    }
    //=========================================================
    // Add source: from pdp page
    //=========================================================
    else if (addToRegistryAttemptInfo.type === 'item') {
      // When user tries to add via PDP page

      const { qty, product, curation, itemPosition, category, telemetryArgs } = addToRegistryAttemptInfo;
      shopAuthenticatePayload = deriveStandardProductPayload({
        ...telemetryArgs,
        eventId,
        product,
        selectedCategory: category,
        storedCuration: curation,
        itemPosition,
        qty,
      });

      additionalQueryParamPayload = {
        qty,
        categoryId: category?.id,
        curationId: curation?.id,
        // Must include storefront when redirecting to an item
        // used in `src/apps/registry/common/components/Catalog/components/ShopStorefrontProvider/ShopStorefrontProvider.tsx`
        shopStorefront: currentStorefront,
      };

      // regardless of storefront, users will always view PDP at `/shop/item`
      shopPathname = `${PUBLIC_SHOP_ORIGIN}/shop/item/${product.id}`;
    } else if (addToRegistryAttemptInfo.type === 'universalSearch') {
      //=========================================================
      // Add source: from universal search pdp modal
      //=========================================================
      shopAuthenticatePayload = {
        eventId,
        method: 'universal search pdp modal',
        page: 'searchResults',
        itemType: CustomItemDataType.gift,
        totalRequested: addToRegistryAttemptInfo.qty,
        productUrl: addToRegistryAttemptInfo.productUrl,
        itemPosition: addToRegistryAttemptInfo.itemPosition,
        shopStorefront: currentStorefront,
        searchTerm: addToRegistryAttemptInfo.searchTerm,
        productSource: universalItemSourceToRegistryItemSource(addToRegistryAttemptInfo.productSource),
        orderId: undefined as unknown as number,
        productCatalogCategoryId: undefined,
        productCatalogCategoryPublicUrlPath: undefined,
        productCatalogCategoryType: undefined,
        productCatalogCategoryTitle: undefined,
        email: undefined,
      };

      shouldSkip = true;
    } else {
      // unhandled -- skip adding query params
      shouldSkip = true;
    }

    if (!shouldSkip && addToRegistryAttemptInfo.type !== 'universalSearch' && addToRegistryAttemptInfo.type !== 'transitionalRegistryBanner') {
      // Construct the redirect url for after create wedding
      shopPathname = `${shopPathname}?${qs.stringify(
        {
          intent: 'add',
          telemetryMethod: addToRegistryAttemptInfo.telemetryArgs.method,
          telemetryPage: addToRegistryAttemptInfo.telemetryArgs.page,
          s: searchQuery,
          ref: refQueryParam,
          instanceId: getInstanceId(),
          ...additionalQueryParamPayload,
        },
        { skipNull: true },
      )}`;
    }
  }

  return {
    postCreateRedirectUrl: shopPathname,
    shopAuthenticatePayload,
  };
};

export const useEventAssociator = (args: UseEventAssociatorArgs) => {
  const { addToRegistryAttemptInfo, membershipStatus } = args;
  const [{ eventId, eventHandle }, setState] = useState<EventAssociatorState>({ eventId: undefined });
  const { formatToUrl } = useFeatureFlagsContext();

  useSetEventHandle(eventHandle);

  const { currentStorefront, config: storefrontConfig } = useShopStorefrontContext();

  const { getValueString } = useQueryParamHelper();

  const { deriveStandardProductPayload } = useCreateAddRegistryItemClickPayload();

  const { eventPickerSelect, eventPickerDialog, shopAuthenticate, updateContext } = usePublicShopTelemetry();

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  const { redirectToFriendsRegistry } = useRedirectToFriendsRegistry();

  const setEvent = useCallback((args: { eventId: string; website: string }) => {
    const { eventId, website } = args;
    setState(prev => ({ ...prev, eventId, eventHandle: website }));
  }, []);

  const tryAndUseEventMembership = useCallback(
    (eventMembership: EventMembership) => {
      const event = eventMembership.event;
      if (event) {
        executeSegmentGroupCallForEvent(event.firebaseId, {
          postgresEventId: event.id,
          eventType: event.info.eventType,
        });
        eventPickerSelect({ role: eventMembership.status, eventId: event.id, membershipsCount: 1 });
        setEvent({ eventId: event.id, website: event.website });
      }
    },
    [setEvent, eventPickerSelect, executeSegmentGroupCallForEvent],
  );

  const redirectUserToCreateWedding = useEventCallback((createMethod: 'event picker dialog' | 'add to registry') => {
    const { shopAuthenticatePayload, postCreateRedirectUrl } = prepareAddAttemptInfo({
      addToRegistryAttemptInfo,
      eventId,
      deriveStandardProductPayload,
      searchQuery: getValueString('s'),
      refQueryParam: getValueString('ref'),
      rootShopPathName: storefrontConfig.rootShopPathname,
      currentStorefront,
    });

    if (shopAuthenticatePayload) {
      shopAuthenticate(shopAuthenticatePayload);
    }

    const redirectUrl = new URL(`${BLISS_APP_ORIGIN}${storefrontConfig.createEventPathname}`);
    redirectUrl.searchParams.set('destination', encodeURIComponent(formatToUrl(postCreateRedirectUrl)));
    redirectUrl.searchParams.set('scenario', 'marcom-shop');
    redirectUrl.searchParams.set('createmethod', createMethod);
    withWindow(global => {
      global.requestIdleCallback(() => {
        global.location.href = formatToUrl(redirectUrl.href);
      });
    });
  });

  const handleOnEventClick = useCallback<UseEventPickerDialogArgs['onEventClick']>(
    args => {
      if (addToRegistryAttemptInfo?.type === 'transitionalRegistryBanner') {
        redirectToFriendsRegistry(args.website);
      } else {
        eventPickerSelect(args);
        setEvent(args);
      }
    },
    [eventPickerSelect, setEvent, redirectToFriendsRegistry, addToRegistryAttemptInfo],
  );

  const handleOnEventPickerOpen = useCallback<NonNullable<UseEventPickerDialogArgs['onOpen']>>(
    memberships => {
      eventPickerDialog.enter(memberships.length);
    },
    [eventPickerDialog],
  );

  const [showEventPickerDialog] = useEventPickerDialog({
    onEventClick: handleOnEventClick,
    membershipStatus,
    currentEventId: eventId,
    onCreateNewEvent: addToRegistryAttemptInfo?.type === 'transitionalRegistryBanner' ? redirectToFriendsRegistry : redirectUserToCreateWedding,
    onOpen: handleOnEventPickerOpen,
    // Only a store add is impossible on an international registry. A cash fund add, or opening the
    // picker with nothing pending, must still be able to reach an international event.
    blockIntlRestrictedEvents: isDeliverableAddAttempt(addToRegistryAttemptInfo),
  });

  const handleOnZeroOrMultipleMatches = useCallback(
    (memberships: EventMembership[]) => {
      if (addToRegistryAttemptInfo) {
        // User tried adding an item, may have 0 or more than 1 owned events.
        if (!memberships.length) {
          // They dont have any owned events.
          if (addToRegistryAttemptInfo.type === 'transitionalRegistryBanner') {
            redirectToFriendsRegistry();
          } else {
            redirectUserToCreateWedding('add to registry');
          }
        } else {
          // Has multiple owned events
          showEventPickerDialog();
        }
      }
    },
    [addToRegistryAttemptInfo, redirectUserToCreateWedding, showEventPickerDialog, redirectToFriendsRegistry],
  );

  useEffect(() => {
    if (eventId) {
      updateContext({ eventId });
    }
  }, [eventId, updateContext]);

  return {
    eventId,
    showEventPickerDialog,
    onSingleMatch: tryAndUseEventMembership,
    onZeroOrMultipleMatches: handleOnZeroOrMultipleMatches,
  };
};
